import { Box, Button, CircularProgress, Divider, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Control, UseFormResetField, UseFormSetError, useFieldArray, useForm, useWatch } from "react-hook-form";
import FormInputText from "../components/Form/FormInputText";
import { QuestionActionEnum, Survey } from "../models/survey.model";
import FormSelection from "../components/Form/FormSelection";
import { enumToArraySelection, onHandleFormSubmit, titleStr } from "../lib/helper";
import { Question, QuestionTypeEnum } from "../models/question.model";
import { AddCircleOutline, ArrowDownward, Close } from "@mui/icons-material";
import FormAutoSearch from "../components/Form/FormAutoSearch";
import "../styles/survey.css";
import { isSelectionType } from "./QuestionForm";
import FormRichEditor from "../components/Form/FormRichEditor";
import { AddTranslationButton } from "./AddTranslation";
import { CompanyStore } from "../models/company.store.model";
import { getCompanyStores } from "../actions/company.store.action";
import _ from "lodash";
import StoreQuestionLogicValue from "../components/StoreQuestionLogicValue";

export default forwardRef(
  (
    props: {
      defaultValues?: Partial<Survey>;
      questions: Question[];
    },
    ref: any
  ) => {
    const { questions } = props;
    const [filedIndex, setFieldIndex] = useState<number>(0);
    const methods = useForm<Survey>({
      defaultValues: props.defaultValues || { questions: [{}] },
    });
    const { handleSubmit, control, setError, setValue, getValues, watch } = methods;
    useImperativeHandle(ref, () => ({
      async onSubmit() {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return onHandleFormSubmit(handleSubmit);
      },
    }));

    const { fields, append, remove, move } = useFieldArray({
      control,
      name: "questions",
      rules: {
        required: "Please add some question to this survey",
      },
    });

    const [loadingStoreOptions, setLoadingStoreOptions] = useState(false);
    const [storeOptions, setStoreOptions] = useState<{ label: string; key: string }[]>([]);

    const loadStores = async () => {
      setLoadingStoreOptions(true);
      let offset: any = undefined;
      let all: CompanyStore[] = [];
      try {
        do {
          const res = await getCompanyStores({
            offset,
            attributes: ["pk", "sk", "data", "name", "area", "region"],
          });
          offset = res.offset;
          all = [...all, ...res.data];
        } while (offset);
      } catch (error) {
        console.error(error);
      } finally {
        const areas = _.uniqBy(all, "area").map((store) => ({
          label: `${titleStr(store.area)}`,
          key: store.area,
        }));
        const regions = _.uniqBy(all, "region").map((store) => ({
          label: `${titleStr(store.region)}`,
          key: store.region,
        }));
        const stores = all.map((store) => ({
          label: `${store.name}`,
          key: store.id,
        }));
        setStoreOptions([...areas, ...regions, ...stores]);
        setLoadingStoreOptions(false);
      }
    };

    useEffect(() => {
      loadStores();
    }, []);

    return (
      <Box component="form" autoComplete="off">
        <Box marginTop={4}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} className="padding-big">
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <div>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <AddTranslationButton
                            onOpen={() => {
                              const translations = getValues("translations");
                              return {
                                keyLabels: [
                                  { key: "description", isHtml: true },
                                  { key: "complete", isHtml: true },
                                  {
                                    key: "storeQuestionText",
                                    label: "Store Question Text",
                                  },
                                  {
                                    key: "storeSearchPlaceholder",
                                    label: "Store Search Placeholder",
                                  },
                                ],
                                translations,
                              };
                            }}
                            onSave={async (trans) => {
                              setValue("translations", trans);
                              window.webApp.showMessage(
                                "success",
                                "Translations saved. Click 'Save Changes' after editing other Question content."
                              );
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="name"
                            control={control}
                            setError={setError}
                            label="Name"
                            required
                            rules={{ required: "Survey name is required" }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormRichEditor
                            editorStyle={{ height: 350 }}
                            prefix="survey/description"
                            name="description"
                            control={control}
                            setError={setError}
                            label="Description"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormRichEditor
                            editorStyle={{ height: 350 }}
                            prefix="survey/complete"
                            name="complete"
                            control={control}
                            setError={setError}
                            label="Complete Content"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormRichEditor
                            editorStyle={{ height: 350 }}
                            prefix="survey/couponcomplete"
                            name="couponComplete"
                            control={control}
                            setError={setError}
                            label="Coupon Complete Content"
                            helperText={
                              "Please use placeholders to replace actual data: {{coupon.code}} {{coupon.title}} {{coupon.description}}"
                            }
                          />
                        </Grid>
                      </Grid>
                    </div>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} className={"padding-big"}>
                <Grid container spacing={4}>
                  <Grid container spacing={4} item xs={12}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className="margin-b-big">
                        Questions
                      </Typography>
                      <Stack direction="column" spacing={2} maxHeight={`calc(100vh - 450px)`} overflow="auto">
                        {fields.map((w, i) => (
                          <div
                            className={`question-form-con ${i === filedIndex ? "active" : ""}`}
                            onClick={() => setFieldIndex(i)}
                            key={`${w.id}`}
                          >
                            <Stack
                              className="margin-b-normal"
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              <Typography variant="subtitle1">Question {i + 1}</Typography>
                              <Stack direction="row" spacing={2}>
                                {i + 1 < fields.length && (
                                  <IconButton
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      move(i, i + 1);
                                    }}
                                  >
                                    <ArrowDownward />
                                  </IconButton>
                                )}
                                {fields.length > 1 && (
                                  <IconButton
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (i === filedIndex) {
                                        setFieldIndex(0);
                                      } else if (filedIndex > i) {
                                        setFieldIndex(filedIndex - 1);
                                      }
                                      remove(i);
                                    }}
                                  >
                                    <Close />
                                  </IconButton>
                                )}
                              </Stack>
                            </Stack>
                            <FormAutoSearch<Question>
                              name={`questions.${i}.question`}
                              label={`Question ${i + 1}`}
                              labelKey={"name"}
                              valueKey={"id"}
                              options={questions}
                              control={control}
                              setError={setError}
                              required
                              rules={{ required: "Please select one question" }}
                            />
                          </div>
                        ))}
                      </Stack>

                      <Button
                        variant="contained"
                        className="initial-text width-100p margin-t-normal"
                        startIcon={<AddCircleOutline />}
                        onClick={() => {
                          append({} as any);
                          setFieldIndex(fields.length);
                        }}
                      >
                        Add new question
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ position: "sticky", top: 32 }} variant="h6" className="margin-b-big">
                        Question {(filedIndex || 0) + 1} Visible Logic
                      </Typography>
                      <div style={{ position: "sticky", top: 64 }}>
                        {loadingStoreOptions ? (
                          <Stack minHeight={200} width="100%" justifyContent="center" alignItems="center">
                            <CircularProgress />
                          </Stack>
                        ) : (
                          <QuestionLogics
                            key={`condition.${filedIndex}`}
                            questions={questions}
                            index={filedIndex}
                            control={control}
                            setError={setError}
                            setValue={setValue}
                            storeOptions={storeOptions}
                          />
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }
);

interface QuestionLogicFormProps {
  index: number;
  questions: Question[];
  control: Control<Survey, any>;
  setError: UseFormSetError<Survey>;
  setValue: any;
  storeOptions: { label: string; key: string }[];
}
function QuestionLogics(props: QuestionLogicFormProps) {
  const { index, control, storeOptions } = props;
  const { fields, append, remove } = useFieldArray({
    control,
    name: `questions.${index}.conditions`,
  });

  return (
    <Stack direction="column" spacing={2}>
      <>
        {fields.map((f, i) => (
          <Stack key={f.id} direction="column" spacing={2}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="subtitle1">Visible Logic {i + 1}</Typography>
              <Stack direction="row" spacing={2}>
                <IconButton color="error" onClick={() => remove(i)}>
                  <Close />
                </IconButton>
              </Stack>
            </Stack>
            <QuestionLogic {...props} i={i} storeOptions={storeOptions} />
            {fields.length > i + 1 ? <Divider>OR</Divider> : null}
          </Stack>
        ))}
        <Button
          variant="outlined"
          className="initial-text width-100p margin-t-normal"
          startIcon={<AddCircleOutline />}
          onClick={() => {
            append({} as any);
          }}
        >
          Add an OR logic
        </Button>
      </>
    </Stack>
  );
}

export const storeQuestionLogicId = "storeId";

function QuestionLogic(props: QuestionLogicFormProps & { i: number; storeOptions: { label: string; key: string }[] }) {
  const { control, setError, index, i, setValue, storeOptions } = props;
  let { questions } = props;
  const storeQuestion = {
    id: storeQuestionLogicId,
    name: "Store Question",
    type: QuestionTypeEnum.dropdown,
    options: storeOptions,
  } as any;
  questions = [...questions, storeQuestion];
  const selectedQuestion = useWatch({
    control,
    name: `questions.${index}.conditions.${i}.question`,
  });

  const selectedAction = useWatch({
    control,
    name: `questions.${index}.conditions.${i}.action`,
  });

  const logicValueName = `questions.${index}.conditions.${i}.value`;
  const logicValueLabel = "Logic Value";
  return (
    <>
      <FormAutoSearch<Question>
        name={`questions.${index}.conditions.${i}.question`}
        label={`Question ${i + 1}`}
        labelKey={"name"}
        valueKey={"id"}
        options={questions}
        control={control}
        setError={setError}
        required
        rules={{ required: "Please select one question" }}
      />
      <FormSelection
        name={`questions.${index}.conditions.${i}.action`}
        label="Logic Action"
        required
        rules={{ required: "Logic action is required" }}
        control={control}
        setError={setError}
        options={
          selectedQuestion?.id === storeQuestionLogicId
            ? enumToArraySelection(QuestionActionEnum).filter((item) => item.value !== QuestionActionEnum.equal)
            : enumToArraySelection(QuestionActionEnum)
        }
        noEmpty
        onChange={() => {
          setValue(logicValueName, "");
        }}
      />
      {selectedQuestion && isSelectionType(selectedQuestion?.type) ? (
        selectedQuestion.id === storeQuestionLogicId ? (
          <StoreQuestionLogicValue
            control={control}
            companyFieldName={`questions.${index}.conditions.${i}.company`}
            logicValueLabel={logicValueLabel}
            logicValueName={logicValueName}
          />
        ) : (
          <FormSelection
            name={logicValueName}
            noEmpty
            key={`questions.${index}.conditions.${i}.action.${selectedAction}`}
            multiple={
              selectedAction === QuestionActionEnum.contain ||
              selectedQuestion.type === QuestionTypeEnum.multipleCheckbox ||
              selectedQuestion.type === QuestionTypeEnum.multipleDropdown
            }
            label={logicValueLabel}
            control={control}
            setError={setError}
            required
            rules={{ required: "Logic value is required" }}
            options={selectedQuestion.options.flatMap((option) => {
              if (selectedQuestion.type === QuestionTypeEnum.multipleDropdown) {
                return [option, ...(option.subOptions || [])].map((sub, index) => {
                  return index === 0
                    ? {
                        value: sub.key,
                        label: sub.label,
                        noCheckbox: true,
                      }
                    : {
                        value: `${option.key}_rr_${sub.key}`,
                        label: sub.label,
                        groupPrefix: selectedAction === QuestionActionEnum.contain ? undefined : option.key,
                      };
                });
              } else {
                return { value: option.key, label: option.label };
              }
            })}
          />
        )
      ) : (
        <FormInputText
          name={logicValueName}
          label={logicValueLabel}
          control={control}
          setError={setError}
          required
          rules={{ required: "Logic value is required" }}
        />
      )}
    </>
  );
}
